import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Locale = 'ar-SA' | 'en-US';
export type Direction = 'rtl' | 'ltr';

export interface ThemeContextValue {
  locale: Locale;
  direction: Direction;
  setLocale: (locale: Locale) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const localeToDirection = (locale: Locale): Direction =>
  locale === 'ar-SA' ? 'rtl' : 'ltr';

export interface ThemeProviderProps {
  initialLocale?: Locale;
  children: ReactNode;
}

export function ThemeProvider({
  initialLocale = 'ar-SA',
  children,
}: ThemeProviderProps): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  // Reflect direction on <html> so Tailwind dir-aware classes work everywhere.
  useEffect(() => {
    const dir = localeToDirection(locale);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
      document.documentElement.dir = dir;
    }
  }, [locale]);

  const value = useMemo<ThemeContextValue>(
    () => ({ locale, direction: localeToDirection(locale), setLocale }),
    [locale, setLocale],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>.');
  }
  return ctx;
}
